import React, { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import {
	QuestionResponse,
	getStoreList,
	getUnansweredQuestions,
	publishQuestionReplies,
} from "../ApiService";
import QuestionCard from "../components/QuestionCard/QuestionCard";
import Button from "../components/Button/Button";
import LoaderCircle from "../components/LoaderCircle/LoaderCircle";
import PageContainer from "../components/PageContainer/PageContainer";
import PageBlock from "../components/PageContainer/PageBlock";
import Modal from "../components/Modal/Modal";
import { useMarketplace } from "../contexts/MarketplaceContext";

type StoredReplies = Record<string, { text: string; sku?: number }>;

// Вспомогательная функция для получения массива ответов с текстом
const getAnsweredReplies = (object: StoredReplies) => {
	return Object.entries(object)
		.filter(([id, reply]) => reply.text.trim())
		.map(([id, reply]) => ({ id, ...reply }));
};

export default function QuestionsPage() {
	const { currentMarketplace, isWB } = useMarketplace();
	const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
	const [replies, setReplies] = useState<StoredReplies>({});
	const [loadingArray, setLoadingArray] = useState<string[]>([]);
	const [allLoading, setAllLoading] = useState(false);
	const [perPage, setPerPage] = useState<number>(10);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [showOnlyUnanswered, setShowOnlyUnanswered] = useState<boolean>(true);
	// TODO: Implement date filtering functionality
	const [dateFrom, setDateFrom] = useState<string>("");
	const [dateTo, setDateTo] = useState<string>("");
	const [isModalOpen, setIsModalOpen] = useState(false);
	const queryClient = useQueryClient();

	const { data: stores } = useQuery(
		["stores", currentMarketplace],
		() => getStoreList(currentMarketplace)
	);

	const {
		data: unansweredQuestions,
		isLoading,
		isError,
		refetch,
		isRefetching,
	} = useQuery<QuestionResponse>(
		["unansweredQuestions", selectedStoreId, perPage, currentPage, showOnlyUnanswered, currentMarketplace],
		() => {
			if (isWB) {
				// Для WB используем классическую логику с isAnswered
				return getUnansweredQuestions({
					page: currentPage,
					perPage: perPage,
					storeId: selectedStoreId!,
					isAnswered: !showOnlyUnanswered, // инвертируем для WB
				});
			} else {
				// Для Ozon загружаем все вопросы и обрабатываем пагинацию на фронте
				return getUnansweredQuestions({
					page: 1,
					perPage: 1000, // Большое значение для получения всех вопросов
					storeId: selectedStoreId!,
					showOnlyUnanswered,
				});
			}
		},
		{
			enabled: !!selectedStoreId,
		}
	);

	const allQuestions = unansweredQuestions?.data.questions || [];

	// Debug logging for Ozon questions
	if (!isWB && allQuestions.length > 0) {
		console.log(`Ozon questions received: ${allQuestions.length} total questions`);
	}

	const startIndex = (currentPage - 1) * perPage;
	const questionList = isWB ? allQuestions : allQuestions.slice(startIndex, startIndex + perPage);
	const marketplaceName = isWB ? "Wildberries" : "Ozon";

	// Для Ozon используем количество загруженных вопросов, для WB - данные с сервера
	const totalQuestions = !isWB
		? allQuestions.length
		: (showOnlyUnanswered
			? unansweredQuestions?.data.countUnanswered
			: unansweredQuestions?.data.countTotal);
	const totalPages = totalQuestions ? Math.ceil(totalQuestions / perPage) : 1;

	// Загрузка кэшированных ответов при монтировании
	useEffect(() => {
		if (selectedStoreId) {
			const cachedReplies = localStorage.getItem(`question_replies_${selectedStoreId}`);
			if (cachedReplies) {
				setReplies(JSON.parse(cachedReplies));
			}
		}
	}, [selectedStoreId]);

	// Сохранение ответов в кэш при изменении
	useEffect(() => {
		if (selectedStoreId && Object.keys(replies).length > 0) {
			localStorage.setItem(`question_replies_${selectedStoreId}`, JSON.stringify(replies));
		}
	}, [replies, selectedStoreId]);

	// Очистка кэша после успешной отправки
	const clearRepliesCache = useCallback(() => {
		if (selectedStoreId) {
			localStorage.removeItem(`question_replies_${selectedStoreId}`);
		}
	}, [selectedStoreId]);

	const handleStoreChange = (event: any) => {
		setSelectedStoreId(event.target.value);
	};

	const handleReplyChange = useCallback(
		(id: string, text: string) => {
			setReplies((prevReplies) => {
				const newReplies = {
					...prevReplies,
					[id]: { text, sku: prevReplies[id]?.sku },
				};
				if (selectedStoreId) {
					localStorage.setItem(`question_replies_${selectedStoreId}`, JSON.stringify(newReplies));
				}
				return newReplies;
			});
		},
		[selectedStoreId]
	);

	const handleSelectReply = (id: string) => {
		// Эта функция больше не нужна, но оставляю для совместимости
	};

	const countAllowedReplies = Object.entries(replies).filter(
		([id, reply]) => reply.text.trim()
	).length;

	const handleSendReply = useCallback(
		async (questionId: string) => {
			const reply = replies[questionId];
			if (!reply?.text?.trim()) return;

			setLoadingArray(prev => [...prev, questionId]);

			try {
				const question = questionList?.find(q => q.id === questionId);
				const sku = question?.sku;

				await publishQuestionReplies({
					replies: [{ id: questionId, text: reply.text, sku }],
					storeId: selectedStoreId!,
				});

				setReplies(prev => {
					const newReplies = { ...prev };
					delete newReplies[questionId];
					if (selectedStoreId) {
						localStorage.setItem(`question_replies_${selectedStoreId}`, JSON.stringify(newReplies));
					}
					return newReplies;
				});

				refetch();
			} catch (error) {
				console.error('Ошибка отправки ответа:', error);
			} finally {
				setLoadingArray(prev => prev.filter(id => id !== questionId));
			}
		},
		[replies, selectedStoreId, questionList, refetch]
	);

	const handleSendAllReplies = useCallback(
		async () => {
			const repliesToSend = getAnsweredReplies(replies);
			if (repliesToSend.length === 0) return;

			setAllLoading(true);

			try {
				const repliesWithSku = repliesToSend.map(reply => {
					const question = questionList?.find(q => q.id === reply.id);
					return {
						id: reply.id,
						text: reply.text,
						sku: question?.sku,
					};
				});

				await publishQuestionReplies({
					replies: repliesWithSku,
					storeId: selectedStoreId!,
				});

				setReplies({});
				clearRepliesCache();
				refetch();
			} catch (error) {
				console.error('Ошибка отправки ответов:', error);
			} finally {
				setAllLoading(false);
			}
		},
		[replies, selectedStoreId, questionList, refetch, clearRepliesCache]
	);

	const countAnsweredReplies = getAnsweredReplies(replies).length;

	useEffect(() => {
		if (stores?.length) {
			setSelectedStoreId(stores[0].id);
		}
	}, [stores]);

	// Сброс состояния при смене маркетплейса
	useEffect(() => {
		setReplies({});
		setSelectedStoreId(null);
		setCurrentPage(1);
	}, [currentMarketplace]);

	// Сброс страницы при изменении параметров
	useEffect(() => {
		setCurrentPage(1);
	}, [selectedStoreId, perPage, showOnlyUnanswered]);

	const hasAllowedReplies = Object.values(replies).filter(
		(reply) => reply.text.trim()
	).length > 0;

	const hasQuestions = questionList && questionList.length > 0;

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	const renderPagination = () => {
		if (totalPages <= 1) return null;

		const maxVisiblePages = 5;
		let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
		let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

		if (endPage - startPage + 1 < maxVisiblePages) {
			startPage = Math.max(1, endPage - maxVisiblePages + 1);
		}

		const pages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

		return (
			<div className="flex items-center gap-2 justify-center mt-4">
				<button
					onClick={() => handlePageChange(currentPage - 1)}
					disabled={currentPage === 1}
					className="px-3 py-1 rounded border disabled:opacity-50 disabled:cursor-not-allowed"
				>
					Назад
				</button>

				{startPage > 1 && (
					<>
						<button
							onClick={() => handlePageChange(1)}
							className="px-3 py-1 rounded border"
						>
							1
						</button>
						{startPage > 2 && <span>...</span>}
					</>
				)}

				{pages.map(page => (
					<button
						key={page}
						onClick={() => handlePageChange(page)}
						className={`px-3 py-1 rounded border ${page === currentPage
							? 'bg-blue-500 text-white'
							: 'hover:bg-gray-100'
							}`}
					>
						{page}
					</button>
				))}

				{endPage < totalPages && (
					<>
						{endPage < totalPages - 1 && <span>...</span>}
						<button
							onClick={() => handlePageChange(totalPages)}
							className="px-3 py-1 rounded border"
						>
							{totalPages}
						</button>
					</>
				)}

				<button
					onClick={() => handlePageChange(currentPage + 1)}
					disabled={currentPage === totalPages}
					className="px-3 py-1 rounded border disabled:opacity-50 disabled:cursor-not-allowed"
				>
					Вперед
				</button>

				<span className="ml-4 text-sm text-gray-600">
					Страница {currentPage} из {totalPages}
				</span>
			</div>
		);
	};

	return (
		<PageContainer>
			{(isLoading || isRefetching) && <LoaderCircle />}

			<PageBlock>
				<h3 className="h2Font mb-4">Вопросы {marketplaceName}</h3>

				{/* Блок управления с улучшенной вёрсткой */}
				<div className="flex flex-col gap-6">
					{/* Верхний ряд: выбор магазина и кнопки действий */}
					<div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
						<select
							value={selectedStoreId || ""}
							onChange={handleStoreChange}
							className="p-2 border rounded w-full md:w-auto min-w-[200px]"
						>
							<option value="" disabled>
								Выберите магазин
							</option>
							{stores?.map((store) => (
								<option key={store.id} value={store.id}>
									{store.name}
								</option>
							))}
						</select>

						<Button
							title="Отправить все"
							status={
								allLoading || countAllowedReplies === 0 || isLoading || isRefetching
									? "disabled"
									: "enabled"
							}
							onClick={handleSendAllReplies}
						/>
					</div>

					{/* Средний ряд: чекбоксы фильтрации */}
					<div className="flex flex-wrap gap-4 items-center">
						<div className="flex items-center gap-2">
							<input
								type="checkbox"
								id="showOnlyUnanswered"
								checked={showOnlyUnanswered}
								onChange={(e) => setShowOnlyUnanswered(e.target.checked)}
								className="w-4 h-4"
							/>
							<label htmlFor="showOnlyUnanswered" className="whitespace-nowrap">Только неотвеченные</label>
						</div>
					</div>

					{/* Нижний ряд: выбор количества вопросов и счётчик */}
					<div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
						<select
							className="p-2 border rounded w-full md:w-auto min-w-[200px]"
							onChange={(event) => setPerPage(parseInt(event.target.value))}
							value={perPage}
						>
							<option value={0} disabled>
								Количество вопросов
							</option>
							{[5, 10, 15, 20, 25, 50, 100].map((count, index) => (
								<option key={index} value={count}>
									{count}
								</option>
							))}
						</select>

						{questionList && (
							<div className="bodyBoldFont">
								Вопросов на странице: {questionList.length} из{" "}
								{totalQuestions !== undefined ? totalQuestions : 0}
							</div>
						)}
					</div>

					{/* Пагинация */}
					<div className="mt-2">
						{renderPagination()}
					</div>

					{isError && <div className="h3Font mt-4">Магазин не настроен</div>}
				</div>
			</PageBlock>

			{/* Отображаем вопросы только если они есть */}
			{questionList && questionList.length > 0 ? (
				questionList.map((question) => (
					<QuestionCard
						key={question.id}
						question={question}
						replyValue={replies[question.id]?.text || ""}
						isLoading={loadingArray.includes(question.id)}
						onSendReply={handleSendReply}
						onReplyChange={handleReplyChange}
					/>
				))
			) : (
				!isLoading && !isRefetching && (
					<div className="flex flex-col items-center justify-center p-8 bg-white rounded-xl shadow-md">
						<p className="text-lg text-gray-600">
							{showOnlyUnanswered
								? "Нет неотвеченных вопросов"
								: "Нет вопросов"}
						</p>
					</div>
				)
			)}

			{isModalOpen && (
				<Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
					<div className="p-4">
						<h3 className="text-lg font-bold mb-4">Информация</h3>
						<p>Ответы успешно отправлены.</p>
					</div>
				</Modal>
			)}
		</PageContainer>
	);
} 